import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import {
  Calendar,
  Radio,
  GraduationCap,
  Award,
  Users,
  Sparkles,
  Globe2,
  Gavel,
  BarChart3,
  ArrowRight,
  Check,
  ChevronDown,
  BookOpen,
  ShieldCheck,
  Heart,
  Briefcase,
  Trophy,
  Building2,
  ScrollText,
  Compass,
  Vote,
} from "lucide-react";

const FEATURES = [
  {
    icon: Calendar,
    title: "Event & Conference Builder",
    blurb: "Spin up a white-label MUN, debate, SDG or hackathon microsite in minutes — branding, agenda, councils, sponsors.",
    img: "https://images.unsplash.com/photo-1561491432-82f2b22a263e",
  },
  {
    icon: Users,
    title: "Delegate Operations",
    blurb: "Registrations, payments, council & country allotment, waitlists, ID badges — all auto-piloted.",
    img: "https://images.unsplash.com/photo-1758270705641-acf09b68a91f",
  },
  {
    icon: GraduationCap,
    title: "Pre-MUN LMS",
    blurb: "Onboard delegates with modular courses, quizzes, assignments and certification tracks.",
    img: "https://images.pexels.com/photos/34947116/pexels-photo-34947116.jpeg",
  },
  {
    icon: Radio,
    title: "Live Session OS",
    blurb: "Real-time speaker list, motion voting, timers and chair scoring — conference grade.",
  },
  {
    icon: Award,
    title: "Awards, Badges & Certificates",
    blurb: "AI-assisted award recommendations and QR-verified certificates that universities trust.",
  },
  {
    icon: BarChart3,
    title: "Analytics & CRM",
    blurb: "Registration funnels, revenue, engagement and post-event marketing — one source of truth.",
  },
];

const SOLUTIONS = [
  { icon: Globe2, label: "Model UN" },
  { icon: Gavel, label: "Debate Championships" },
  { icon: Vote, label: "Youth Parliaments" },
  { icon: Trophy, label: "SDG & Climate Summits" },
  { icon: BookOpen, label: "School Conferences" },
  { icon: Compass, label: "Hackathons" },
  { icon: Sparkles, label: "Leadership Summits" },
  { icon: Building2, label: "University Conferences" },
];

const JOURNEY = [
  { step: "01", label: "Registered", note: "One-click registration with school discount codes." },
  { step: "02", label: "Trained", note: "Pre-MUN LMS, quizzes & badge unlocks." },
  { step: "03", label: "Prepared", note: "AI prep assistant tailored to your country & agenda." },
  { step: "04", label: "Attended", note: "Live attendance, sessions, motions & voting." },
  { step: "05", label: "Assessed", note: "Chair scores + AI leadership scorecard." },
  { step: "06", label: "Certified", note: "QR-verified certificate hand-delivered." },
  { step: "07", label: "Alumni", note: "Lifelong global passport with leaderboard rank." },
];

const STAKEHOLDER_PILLARS = [
  {
    eyebrow: "For schools & faculty",
    icon: Building2,
    title: "Coordinator dashboard with global exposure reports.",
    blurb: "Track every delegate, every payment, every certificate. Hand parents a beautifully-designed Global Exposure Report at the end of every season.",
    stat: "32+ partner schools",
    img: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1",
  },
  {
    eyebrow: "For parents",
    icon: Heart,
    title: "Visibility, safety & celebration in one place.",
    blurb: "See your child's schedule, attendance, training progress and certificates. Get safety alerts during travel and celebrate every award the moment it's announced.",
    stat: "98% parent NPS",
    img: "https://images.unsplash.com/photo-1543269865-cbf427effbad",
  },
  {
    eyebrow: "For sponsors",
    icon: Briefcase,
    title: "Premium brand placement with measurable ROI.",
    blurb: "Logo placements, sponsored awards, lead capture forms and a downloadable post-conference ROI report — sponsors finally see why they invested.",
    stat: "12+ recurring sponsors",
    img: "https://images.unsplash.com/photo-1556761175-b413da4baf72",
  },
];

const TESTIMONIALS = [
  {
    quote: "Diplomexa replaced five tools. Our secretariat ran a 600-delegate conference with zero WhatsApp chaos.",
    name: "Riya Kapoor",
    role: "Secretary-General, Globex MUN",
  },
  {
    quote: "The AI evaluation on position papers changed how our chairs brief delegates. Unreal.",
    name: "Arjun Mehta",
    role: "Director, UNSC Simulation",
  },
  {
    quote: "The QR certificate verification is a legitimacy moat. Parents and universities finally trust us.",
    name: "Kabir Rao",
    role: "Founder, Harmony Youth Parliament",
  },
];

const SCALE_PILLS = [
  { v: "18 countries", k: "Conferences hosted" },
  { v: "60+ schools", k: "Connected institutions" },
  { v: "200+ chairs", k: "Co-built with us" },
  { v: "ZERO downtime", k: "During live conferences" },
];

const FAQ = [
  {
    q: "Is Diplomexa free for student-led conferences?",
    a: "Yes — your first 100-delegate conference is completely free. We only ask for credit when you cross 100 paid registrations or want AI features.",
  },
  {
    q: "Does it work for online & hybrid events?",
    a: "Every event has an offline / online / hybrid toggle. Google Meet & Zoom placeholders flip to real OAuth integrations on request.",
  },
  {
    q: "Are the certificates verifiable?",
    a: "Every certificate has a unique QR code that resolves to a public verification page — universities trust the link, not the PDF.",
  },
  {
    q: "How do you handle parent visibility?",
    a: "Parents get their own dashboard: schedule, attendance, training progress, certificates and safety alerts.",
  },
  {
    q: "Can sponsors track ROI?",
    a: "Sponsors get a private portal with logo placement preview, lead capture forms and a downloadable ROI summary post-event.",
  },
];

export default function Landing() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events/public").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <PublicShell>
      {/* Hero — Editorial Luxury */}
      <section className="relative overflow-hidden bg-brand-navy-dark">
        {/* Layered backdrop: archival photograph at 22 % + dark gold-tinted gradient */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url("https://images.unsplash.com/photo-1725618878496-233974f2fd59")` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy/90 to-brand-navy-dark/95" />
        {/* Gold particle dust hint */}
        <div aria-hidden className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -left-32 w-[32rem] h-[32rem] rounded-full bg-brand-midblue/20 blur-3xl" />
        <div className="bg-noise absolute inset-0 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-36 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/5 border border-brand-gold/30 mb-8">
              <Sparkles className="h-3 w-3 text-brand-gold" />
              <span className="eyebrow">
                The Global Conference & Leadership OS
              </span>
            </div>
            <h1 className="headline-luxury text-5xl sm:text-6xl lg:text-[5.25rem] text-brand-champagne">
              Run smarter
              <span className="block">
                conferences.
              </span>
              <span className="italic text-brand-gold font-display font-medium">
                Build global citizens.
              </span>
            </h1>
            <div className="divider-gold-hairline mt-10 max-w-md" />
            <p className="mt-8 text-base sm:text-lg text-brand-champagne/70 max-w-2xl leading-relaxed">
              Diplomexa helps organisers, schools, delegates, parents, sponsors and partners
              manage, automate, assess, certify and scale global youth conferences — from a
              one-time event into a lifelong leadership journey.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-start-button">
                <Button className="h-12 px-7 rounded-sm bg-brand-gold text-brand-navy-dark hover:bg-brand-gold-light font-bold text-xs uppercase tracking-[0.2em] shadow-gold-soft hover:shadow-gold-strong transition-all duration-300">
                  Start your first event
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/demo" data-testid="hero-demo-button">
                <Button
                  variant="outline"
                  className="h-12 px-7 rounded-sm border-brand-gold/30 bg-transparent text-brand-champagne hover:bg-brand-gold/10 hover:border-brand-gold hover:text-brand-champagne font-semibold text-xs uppercase tracking-[0.2em] transition-all duration-300"
                >
                  Book a demo
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-2xl">
              {[
                ["18k+", "Delegates hosted"],
                ["240+", "Councils simulated"],
                ["32",   "Partner schools"],
                ["4.9★", "Organiser rating"],
              ].map(([k, v], i) => (
                <div key={v} className={`fade-up-delay-${(i % 4) + 1}`}>
                  <p className="font-display text-4xl text-brand-gold leading-none">{k}</p>
                  <p className="eyebrow mt-2.5 text-brand-champagne/45">{v}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Floating VIP Pass card */}
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -1.5 }}
            animate={{ opacity: 1, y: 0, rotate: -1.5 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ rotate: 0, y: -6 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="glass-dark rounded-sm p-7 gold-glow relative overflow-hidden">
              {/* Foil-stripe top */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-80" />
              <div className="flex items-center justify-between mb-5">
                <span className="eyebrow text-brand-gold">LIVE · UNSC</span>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-gold-pulse" />
                  <span className="font-mono text-[10px] text-brand-champagne/80 tracking-wider">IN SESSION</span>
                </div>
              </div>
              <h3 className="font-display text-2xl text-brand-champagne leading-tight">
                South China Sea
                <span className="block text-brand-champagne/60 text-base font-sans font-medium mt-1">
                  Moderated caucus · 2 min 12 sec
                </span>
              </h3>
              <div className="divider-gold-hairline my-6" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="eyebrow text-brand-champagne/45 mb-1.5">Current speaker</p>
                    <p className="text-brand-champagne text-sm font-semibold">USA — Delegate Verma</p>
                  </div>
                  <div className="text-right">
                    <p className="eyebrow text-brand-champagne/45 mb-1.5">Score</p>
                    <p className="font-display text-2xl text-brand-gold">9.4</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {["UK", "FR", "CN"].map((c) => (
                    <div key={c} className="text-center py-2 rounded-sm bg-white/5 border border-brand-gold/10">
                      <p className="text-brand-champagne text-xs font-semibold">{c}</p>
                      <p className="text-[10px] text-brand-champagne/40 font-mono mt-0.5">queued</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Watermark — Diplomexa */}
            <p className="mt-4 text-center eyebrow text-brand-champagne/30">
              · Diplomexa Live Session OS ·
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ticker — diplomatic legacy bodies */}
      <section className="relative bg-brand-navy-dark border-y border-brand-gold/15">
        <div className="overflow-hidden">
          <div className="ticker flex items-center gap-16 py-4 whitespace-nowrap">
            {[...Array(2)].flatMap((_, i) =>
              ["UN SECURITY COUNCIL", "G20 SIMULATION", "LOK SABHA", "ECOSOC", "ARAB LEAGUE", "EUROPEAN UNION", "AFRICAN UNION", "UNHRC", "WHO", "UNGA"].map((t) => (
                <span key={`${i}-${t}`} className="text-[11px] uppercase tracking-[0.28em] text-brand-champagne/40 font-semibold">
                  {t} <span className="text-brand-gold mx-8">◆</span>
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Solutions strip */}
      <section className="max-w-7xl mx-auto px-6 py-20" id="solutions">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Use cases</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Built for <span className="font-serif italic text-[#0F4C81]">every</span> kind of student conference.
            </h2>
          </div>
          <Link to="/solutions" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#0F4C81] hover:text-[#0A192F]">
            See all solutions <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {SOLUTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                to="/solutions"
                key={s.label}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all flex items-center gap-3"
                data-testid={`solution-tile-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:text-[#0A192F] transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-sm font-semibold text-[#0A192F]">{s.label}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-slate-200 bg-[#f6f8fb]">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">The Platform</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0A192F] leading-tight">
              One operating system.<br />
              <span className="font-serif italic text-[#0F4C81]">Every stage</span> of your conference.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
              From the landing page of your conference to the final certificate handed to a delegate,
              Diplomexa covers every click, payment, motion, score and badge.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            {FEATURES.map((f, idx) => {
              const Icon = f.icon;
              const span = idx === 0 ? "md:col-span-7 md:row-span-2" : idx === 1 ? "md:col-span-5" : "md:col-span-4";
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className={`group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-all ${span}`}
                >
                  {f.img && (
                    <div className="absolute inset-0 opacity-10 bg-cover bg-center group-hover:opacity-20 transition-opacity" style={{ backgroundImage: `url(${f.img})` }} />
                  )}
                  <div className="relative">
                    <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold tracking-tight text-[#0A192F]">{f.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-md">{f.blurb}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Delegate journey */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">The Delegate Journey</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Seven steps from <span className="font-serif italic text-[#0F4C81]">first speech</span> to <span className="font-serif italic text-[#0F4C81]">global alumni</span>.
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">
            Every delegate gets a lifelong passport that grows with every conference, course, paper and award.
          </p>
        </div>
        <div className="mt-14 relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-5">
            {JOURNEY.map((j, i) => (
              <motion.div
                key={j.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="relative"
              >
                <div className="flex flex-col items-start">
                  <div className="relative z-10 h-10 w-10 rounded-full bg-[#0A192F] border-2 border-[#D4AF37] text-[#D4AF37] flex items-center justify-center text-xs font-bold tracking-tighter">
                    {j.step}
                  </div>
                  <p className="mt-4 text-base font-semibold tracking-tight text-[#0A192F]">{j.label}</p>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{j.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholder pillars */}
      <section id="stakeholders" className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 space-y-16">
          {STAKEHOLDER_PILLARS.map((p, idx) => {
            const Icon = p.icon;
            const reverse = idx % 2 === 1;
            return (
              <div key={p.title} className={`grid lg:grid-cols-12 items-center gap-10 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="lg:col-span-5 [direction:ltr]"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/15 text-[#0A192F] text-[10px] uppercase tracking-[0.2em] font-bold">
                    <Icon className="h-3 w-3" /> {p.eyebrow}
                  </div>
                  <h3 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tighter text-[#0A192F] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-base text-slate-600 leading-relaxed">{p.blurb}</p>
                  <p className="mt-6 font-serif text-2xl text-[#D4AF37]">{p.stat}</p>
                </motion.div>
                <div className="lg:col-span-7 [direction:ltr]">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-slate-200 bg-cover bg-center"
                    style={{ backgroundImage: `url(${p.img})` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Live events */}
      <section id="events" className="bg-[#f6f8fb] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Now on Diplomexa</p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">Live & upcoming conferences</h2>
            </div>
            <Link to="/register" data-testid="host-event-link">
              <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white">
                Host your own →
              </Button>
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {events.length === 0 && <p className="text-slate-500 text-sm">Loading conferences…</p>}
            {events.slice(0, 4).map((ev) => (
              <Link
                key={ev.id}
                to={`/events/${ev.slug}`}
                data-testid={`event-card-${ev.slug}`}
                className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-md transition-all"
              >
                <div className="h-56 bg-cover bg-center" style={{ backgroundImage: `url(${ev.banner_url})` }} />
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-[10px] uppercase tracking-[0.18em] font-bold">
                  {ev.format}
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-500">
                    {new Date(ev.start_date).toLocaleDateString()} · {ev.location}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold tracking-tight text-[#0A192F] group-hover:text-[#0F4C81] transition-colors">
                    {ev.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{ev.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[#0F4C81] font-semibold text-sm">₹{ev.price?.toLocaleString?.() || 0}</span>
                    <span className="text-xs text-slate-500">Max {ev.max_delegates} delegates</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Voices from the Dais</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-tight">
              Built with <span className="gold-gradient-text font-serif italic">the people</span> who run MUNs.
            </h2>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative rounded-xl p-6 border border-white/10 bg-white/5 ${i === 2 ? "md:col-span-2" : ""}`}
              >
                <Gavel className="absolute top-4 right-4 h-5 w-5 text-[#D4AF37]/40" />
                <p className="text-base text-slate-100 leading-relaxed font-serif italic">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global scalability band */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Built to scale</p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F] leading-tight">
                Same platform from your <span className="font-serif italic text-[#0F4C81]">first 50</span> to your <span className="font-serif italic text-[#0F4C81]">5000th delegate</span>.
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Multi-tenant architecture, multi-event seasons, multi-currency support and SOC-2-ready
                infrastructure. We've stress-tested for 10x your peak — so your conference never blinks.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {["SOC-2 ready", "Multi-currency", "Multi-language coming soon", "99.9% uptime SLA", "GDPR-ready"].map((b) => (
                  <span key={b} className="px-3 py-1.5 rounded-full bg-[#0A192F] text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] font-semibold">
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-3">
              {SCALE_PILLS.map((p) => (
                <div key={p.k} className="rounded-xl border border-slate-200 bg-[#f6f8fb] p-5 text-center">
                  <p className="font-serif text-2xl text-[#0F4C81]">{p.v}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{p.k}</p>
                </div>
              ))}
              <div className="col-span-2 md:col-span-4 rounded-xl bg-[#0A192F] text-white p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
                  <p className="text-sm">Your data lives in encrypted, region-pinned buckets with full audit logs.</p>
                </div>
                <Link to="/contact">
                  <Button className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]">
                    Get DPA pack
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">FAQ</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Quick answers before you ask.
          </h2>
        </div>
        <div className="mt-12 space-y-3" data-testid="landing-faq">
          {FAQ.map((f, i) => (
            <FaqRow key={i} q={f.q} a={f.a} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/pricing" className="text-sm font-semibold text-[#0F4C81] hover:text-[#0A192F]">
            See full FAQ on pricing page →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden border-t border-slate-200">
        <div className="absolute inset-0 bg-[#0A192F]" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tighter text-white">
              Your next conference starts <span className="gold-gradient-text font-serif italic">today</span>.
            </h2>
            <p className="mt-5 text-slate-300 max-w-lg">
              Onboard in under 10 minutes. No credit card. Your first 100 delegates are free.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 md:justify-end">
            <Link to="/register">
              <Button className="h-12 px-6 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]" data-testid="final-cta-register">
                Create your org
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="outline" className="h-12 px-6 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="final-cta-demo">
                Book a demo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left px-5 py-4">
        <span className="text-[15px] font-semibold text-[#0A192F]">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
